import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BalanceCoin } from '../icons/BalanceCoin'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { isStaff } from '../utils/roles'
import { NAV_LAYOUT_DEFAULTS, navBadge, navMode, navVisibleInMenu } from '../api/nav'
import { useNavModes } from '../hooks/useNavModes'
import { buildNavTree } from '../utils/navTree'
import type { Model } from '../data/models'
import HeaderActions from './HeaderActions'
import { useAccountNavigation } from '../hooks/useAccountNavigation'
import SidebarChats from './SidebarChats'
import { useSidebarSessions } from '../hooks/useSidebarSessions'
import SidebarQuickMenu from './SidebarQuickMenu'
import NotificationPanel from './NotificationPanel'
import { useNotifications } from '../context/NotificationContext'
import { useGlobalGeneration } from '../context/GlobalGenerationContext'
import { useProviders } from '../hooks/useProviders'

type Props = {
	isOpen: boolean
	onClose: () => void
	refreshKey?: number
	onSelectModel?: (model: Model | null) => void
	collapsed: boolean
	onToggleCollapsed: React.Dispatch<React.SetStateAction<boolean>>
	iconVersion?: number
}

// staffOnly — мягкий запуск раздела: пункт виден только стаффу (isStaff).
// colored — цветная 3D-иконка (флагман «Тренды»): рендерится <img> без маски,
// чтобы сохранить градиенты (обычные иконки — монохром-маска в currentColor).
const BOTS_SUBMENU: {
	/** id пункта для настройки видимости из админки («Настройки → Меню»).
	 *  Совпадает с NAV_ITEMS в backend/app/services/nav_visibility.py и с
	 *  NAV_DEFAULTS в api/nav.ts — по нему матчится режим all/staff/off. */
	id: string
	path: string
	labelKey: string
	icon: string
	/** Поведение из кода, когда настройки нет: дублируется в NAV_DEFAULTS. */
	staffOnly?: boolean
	colored?: boolean
	// Ярлык «new» больше не из кода: вешается в админке «Меню сайта» (владелец 15.10), см. navBadge в api/nav.ts.
	// Порядок и вложенность (вкладки «Лабораторий» и т.п.) — тоже не из кода: раскладка из «Меню сайта»
	// (владелец 15.10: перетаскивание), utils/navTree.ts; пока карты нет — NAV_LAYOUT_DEFAULTS.
}[] = [
	// ?v=2 — cache-bust: файл в public/ не хэшируется Vite и отдаётся с
	// Cache-Control 30д под стабильным URL. При смене иконки бампаем версию,
	// иначе браузеры держат старую до 30 дней (обычный F5 не перезапрашивает).
	// «Сцена» — страница-студия по образцу Artlist Studio (владелец 30.09): staffOnly до проверки, режим — в «Меню».
	{ id: 'scene', path: '/scene', labelKey: 'nav.bots.scene', icon: '/icons/nav_scene.svg', staffOnly: true },
	{ id: 'trends', path: '/bots/trends', labelKey: 'nav.bots.trends', icon: '/icons/nav_trends.svg?v=2', colored: true },
	// «Умные помощники» — новый раздел параллельно старым ИИ-помощникам (владелец 06.09);
	// staffOnly до живой проверки, бэк дополнительно закрыт флагом assistants.enabled.
	{ id: 'smart_assistants', path: '/assistants', labelKey: 'nav.bots.smartAssistants', icon: '/icons/nav_ai.svg', staffOnly: true },
	{ id: 'assistants', path: '/bots/assistants', labelKey: 'nav.bots.assistants', icon: '/icons/nav_ai.svg' },
	{ id: 'images', path: '/bots/images', labelKey: 'nav.bots.images', icon: '/icons/nav_img.svg' },
	{ id: 'video', path: '/bots/video', labelKey: 'nav.bots.video', icon: '/icons/nav_video.svg' },
	{ id: 'audio', path: '/bots/audio', labelKey: 'nav.bots.audio', icon: '/icons/nav_audio.svg' },
	// «Мастерская» (флоу) — узловой холст генераций (владелец 04.09: отдельный раздел; 15.10: ниже, рядом с
	// «Лабораториями», без ярлыка «new»); staffOnly до конца фазы 1, бэк дополнительно закрыт флагом flows.enabled.
	{ id: 'flows', path: '/flows', labelKey: 'nav.bots.flows', icon: '/icons/nav_flows.svg', staffOnly: true },
	// «Лаборатории» и их вкладки по умолчанию (NAV_LAYOUT_DEFAULTS): карточка товара (владелец 15.10, до этого над ними
	// с 08.10), обложки, презентации (переехали из /bots/slides).
	{ id: 'tools', path: '/bots/tools', labelKey: 'nav.bots.tools', icon: '/icons/nav_lab.svg' },
	{ id: 'product_card', path: '/bots/tools/product-card', labelKey: 'nav.bots.productCard', icon: '/icons/nav_product_card.svg' },
	{ id: 'covers', path: '/bots/tools/covers', labelKey: 'nav.bots.covers', icon: '/icons/nav_audio.svg' },
	{ id: 'slides', path: '/bots/tools/slides', labelKey: 'nav.bots.slides', icon: '/icons/nav_slides.svg' },
]

// У «Карточки товара» два равнозначных маршрута: дефис — из этого меню, нижнее
// подчёркивание — из каталога «Лабораторий» (там путь строится по slug'у
// пайплайна). Подсвечиваем вкладку на обоих, иначе заход из каталога оставлял бы
// меню без активного раздела.
const PRODUCT_CARD_PATHS = ['/bots/tools/product-card', '/bots/tools/product_card']

// Дефолтные модели для кнопки «Новый чат» — module-level константа (стабильна
// между рендерами, поэтому корректно НЕ входит в deps useMemo'а defaultTextModel).
const DEFAULT_CHAT_MODEL_IDS = ['gpt-5.4', 'gpt-4o', 'gpt-4.1']

// Сворачиваемая группа статичного меню (аккордеон). Рендерится ТОЛЬКО в
// «компактном» режиме (низкая высота окна + не мобилка), где плоское меню не
// помещается и прячет чаты. Заголовок-кнопка + плавное раскрытие тела через
// grid-rows 0fr↔1fr. На десктопе/мобилке меню плоское — этот компонент не
// используется (см. Sidebar render).
type NavGroupProps = {
	title: string
	/** Глиф для СЖАТОЙ панели: текстовый заголовок там обрезался до «АК»/«ИН»
	 *  (фото владельца, планшет) — вместо него иконка с тултипом. */
	iconSrc: string
	open: boolean
	onToggle: () => void
	children: React.ReactNode
}

const NavGroup = ({ title, iconSrc, open, onToggle, children }: NavGroupProps) => (
	<div className={`nav-group ${open ? 'is-open' : 'is-collapsed'}`}>
		<button type="button" className="nav-group-header" onClick={onToggle} aria-expanded={open} title={title}>
			<svg className="nav-group-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
				<path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
			</svg>
			<span className="nav-group-icon" aria-hidden="true" style={{ '--icon': `url(${iconSrc})` } as React.CSSProperties} />
			<span className="nav-group-title">{title}</span>
		</button>
		<div className="nav-group-body">
			<div className="nav-group-items">{children}</div>
		</div>
	</div>
)

const Sidebar = ({
	isOpen,
	onClose,
	refreshKey,
	onSelectModel,
	collapsed,
	onToggleCollapsed,
	iconVersion: _iconVersion,
}: Props) => {
	const { t } = useTranslation()
	const { theme } = useTheme()
	const { user, logout } = useAuth()
	// Видимость пунктов задаёт админка («Настройки → Меню»); пока карта не пришла,
	// работает поведение из кода (NAV_DEFAULTS), поэтому меню не мигает.
	const { modes: navModes, badges: navBadges, layout: navLayout } = useNavModes()
	// Дерево меню: порядок и вложенность — из раскладки админки (владелец 15.10), иначе как в коде.
	const navTree = useMemo(() => buildNavTree(BOTS_SUBMENU, navLayout, NAV_LAYOUT_DEFAULTS), [navLayout])
	const navigate = useNavigate()
	const location = useLocation()
	const { hasUnseenResult, markResultSeen } = useGlobalGeneration()
	const { allModels } = useProviders()
	const defaultTextModel = useMemo(
		() =>
			allModels.find((m) => m.category === 'text' && DEFAULT_CHAT_MODEL_IDS.includes(m.id)) ??
			allModels.find((m) => m.category === 'text') ??
			null,
		[allModels],
	)

	useEffect(() => {
		if (location.pathname === '/files' && hasUnseenResult) {
			markResultSeen()
		}
	}, [location.pathname, hasUnseenResult, markResultSeen])

	const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)
	// Низкая высота окна — диапазон, где плоское меню (10 пунктов) выдавливает
	// чаты в ноль (планшеты-ландшафт, нетбуки, короткие окна ноутбуков).
	const [isShortHeight, setIsShortHeight] = useState(() => window.innerHeight <= 740)
	const [notificationsOpen, setNotificationsOpen] = useState(false)
	const { markAllRead, unreadCount } = useNotifications()

	// Компактный режим = НЕ мобилка (там свой drawer) И низкая высота. Только в
	// нём статичное меню превращается в аккордеоны «Аккаунт» / «ИИ-инструменты».
	// Десктоп (высокий) и мобильный drawer остаются как раньше (плоское меню).
	const isCompactNav = !isMobile && isShortHeight

	const [navGroupsOpen, setNavGroupsOpen] = useState<{ account: boolean; tools: boolean }>(() => {
		try {
			const saved = localStorage.getItem('umnik.sidebarNavGroups')
			if (saved) return { account: false, tools: false, ...JSON.parse(saved) }
		} catch {
			/* localStorage недоступен — дефолт ниже */
		}
		return { account: false, tools: false } // по умолчанию свёрнуто → чаты сразу видны
	})

	const toggleNavGroup = (key: 'account' | 'tools') => {
		setNavGroupsOpen((prev) => {
			const next = { ...prev, [key]: !prev[key] }
			try {
				localStorage.setItem('umnik.sidebarNavGroups', JSON.stringify(next))
			} catch {
				/* запоминание необязательно */
			}
			return next
		})
	}

	const icon = (name: string) => `/icons/${name}_${theme}.svg`

	const handleAccountClick = useAccountNavigation()

	const handleLogout = () => {
		logout()
		navigate('/login')
		onClose()
	}

	const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (isMobile) {
			onClose()
			return
		}
		const button = event.currentTarget
		onToggleCollapsed((prev) => !prev)
		setTimeout(() => {
			button.blur()
		}, 0)
	}

	const navigateToChatRoot = () => navigate('/chat')

	const { sessions, deleteOne, pinOne, renameOne, hasMore, loadMore } = useSidebarSessions({
		userExists: Boolean(user),
		refreshKey,
		currentPathname: location.pathname,
		navigateToChatRoot,
	})

	useEffect(() => {
		const onResize = () => {
			setIsMobile(window.innerWidth <= 768)
			setIsShortHeight(window.innerHeight <= 740)
		}
		window.addEventListener('resize', onResize)
		return () => window.removeEventListener('resize', onResize)
	}, [])

	useEffect(() => {
		if (isMobile && isOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = ''
		}
		return () => { document.body.style.overflow = '' }
	}, [isMobile, isOpen])

	// Сбрасываем панель уведомлений при закрытии сайдбара. Делаем во время
	// рендера (паттерн React «adjust state on prop change»), а не в эффекте —
	// иначе react-hooks/set-state-in-effect и лишний каскадный ре-рендер.
	const [prevSidebarOpen, setPrevSidebarOpen] = useState(isOpen)
	if (prevSidebarOpen !== isOpen) {
		setPrevSidebarOpen(isOpen)
		if (!isOpen) setNotificationsOpen(false)
	}

	const currentSessionId = useMemo(() => {
		if (location.pathname.startsWith('/chat/')) return location.pathname.split('/chat/')[1]
		return null
	}, [location.pathname])
	// Помощник в меню (23.10) подсвечен, пока открыт его раздел: /assistants/<слуг> в любом состоянии.
	const currentAgentSlug = useMemo(() => /^\/assistants\/([^/?#]+)/.exec(location.pathname)?.[1] ?? null, [location.pathname])

	const normalizedPath = useMemo(() => {
		return location.pathname.replace(/\/+$/, '') || '/'
	}, [location.pathname])

	const isChatRootPage = normalizedPath === '/' || normalizedPath === '/chat'

	const handleNewChat = () => {
		if (onSelectModel) onSelectModel(defaultTextModel)
		if (!isChatRootPage) navigate('/chat')
		onClose()
	}

	const handleOpenSession = (
		sessionId: string,
		agentInfo?: {
			agentId?: string | null
			agentTitle?: string | null
			agentIconUrl?: string | null
		},
		kind?: 'chat' | 'assistant',
		row?: { agent_slug?: string | null; run_id?: string | null },
	) => {
		// Помощник в меню (23.10): открывается его последняя работа на странице помощников (со знаниями, инструментами и
		// документами), а не /chat.
		if (kind === 'assistant') {
			navigate(row?.agent_slug ? `/assistants/${row.agent_slug}${row.run_id ? `?run=${row.run_id}` : ''}` : '/assistants')
			onClose()
			return
		}
		// Если у сессии есть агент — пробрасываем его в Chat через state,
		// чтобы заголовок помощника отрисовался мгновенно (а не после
		// загрузки сессии с бэка). Без state'а Chat ~200ms показывал бы
		// предыдущую модель (типа ChatGPT) пока loadSession резолвится.
		const hasAgent = Boolean(agentInfo?.agentId || agentInfo?.agentTitle || agentInfo?.agentIconUrl)
		if (hasAgent) {
			navigate(`/chat/${sessionId}`, {
				state: {
					agentId: agentInfo?.agentId ?? '',
					agentTitle: agentInfo?.agentTitle ?? '',
					agentIconUrl: agentInfo?.agentIconUrl ?? '',
				},
			})
		} else {
			navigate(`/chat/${sessionId}`)
		}
		onClose()
	}

	// Открытие колокольчика в меню телефона отмечает прочитанным, как колокольчик шапки на компьютере (HeaderActions):
	// раньше здесь панель только открывалась — уведомления оставались непрочитанными и на сервере, и в «Прочитали»
	// рассылок админки (24.10).
	const toggleNotifications = () => {
		if (!notificationsOpen) markAllRead()
		setNotificationsOpen((prev) => !prev)
	}
	const closeNotifications = () => setNotificationsOpen(false)

	const handleBotsClick = () => {
		navigate('/bots')
		if (isMobile) onClose()
	}

	// Пункты статичного меню вынесены в две группы, чтобы переиспользовать их
	// и в плоской раскладке (десктоп/мобилка), и в аккордеонах (компактный
	// режим) — без дублирования разметки.
	const accountItems = (
		<>
			<button
				className={`nav-item new-chat-btn ${isChatRootPage ? 'active' : ''}`}
				onClick={handleNewChat}
				title={t('nav.newChat')}
				type="button"
			>
				<span className="nav-item-box">
					<span className="nav-icon" style={{ '--icon': `url(${icon('pencil')})` } as React.CSSProperties} />
					<span className="nav-label">{t('nav.newChat')}</span>
				</span>
			</button>

			<NavLink
				to="/files"
				className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
				onClick={onClose}
				title={t('nav.myFiles')}
			>
				<span className="nav-item-box">
					<span className="nav-icon" style={{ '--icon': `url(${icon('folder')})` } as React.CSSProperties} />
					<span className="nav-label">{t('nav.myFiles')}</span>
					{hasUnseenResult && <span className="nav-unseen-dot" />}
				</span>
			</NavLink>

			<NavLink
				to="/generate"
				className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
				onClick={onClose}
				title={t('nav.generator')}
			>
				<span className="nav-item-box">
					<span className="nav-icon" style={{ '--icon': `url(/icons/blackstar_gen.svg)` } as React.CSSProperties} />
					<span className="nav-label">{t('nav.generator')}</span>
				</span>
			</NavLink>
		</>
	)

	// Группа с вкладками (сегодня «Лаборатории», завтра любая — раскладка из админки): клик по голове открывает И
	// подсвечивает; переход на другой пункт (= смена маршрута) закрывает сам (владелец 2026-08-14). Деривация без
	// эффектов: помним, КАКУЮ группу и НА КАКОМ экране открыли кликом. Внутри вкладки группа раскрыта и подсвечена
	// всегда — судим по составу группы, а не по началу адреса (владелец 15.10: адреса от вложенности не зависят).
	const [openedGroup, setOpenedGroup] = useState<{ id: string; at: string } | null>(null)
	const isPathOf = (def: { id: string; path: string }) =>
		normalizedPath === def.path || normalizedPath.startsWith(`${def.path}/`) ||
		(def.id === 'product_card' && PRODUCT_CARD_PATHS.includes(normalizedPath))

	const toolsItems = (
		<>
			<NavLink
				to="/bots"
				className={({ isActive }) => `nav-item ${isActive && normalizedPath === '/bots' ? 'active' : ''}`}
				onClick={handleBotsClick}
				title={t('nav.allModels')}
			>
				<span className="nav-item-box">
					<span className="nav-icon" style={{ '--icon': `url(${icon('dots')})` } as React.CSSProperties} />
					<span className="nav-label">{t('nav.allModels')}</span>
				</span>
			</NavLink>

			{navTree.filter(({ item }) => navVisibleInMenu(navMode(navModes, item.id), isStaff(user?.role))).map(({ item, children: rawChildren }) => {
				const children = rawChildren.filter((child) => navVisibleInMenu(navMode(navModes, child.id), isStaff(user?.role)))
				// Группа с вкладками: в развёрнутом сайдбаре голова — кнопка-тоггл (не ссылка), вкладки — вложенные
				// NavLink. В СЖАТОЙ колонке подменю негде жить — голова остаётся ссылкой на свою страницу (иконка с тултипом).
				if (children.length > 0 && !collapsed) {
					const inGroup = children.some(isPathOf) || isPathOf(item)
					const expanded = inGroup || (openedGroup?.id === item.id && openedGroup.at === normalizedPath)
					return (
						<div key={item.path} className={`nav-subtree ${expanded ? 'is-open' : ''}`}>
							<button
								type="button"
								className={`nav-item nav-subtree__head ${
									// Полная пилюля — только когда внутри ВЫБРАН раздел;
									// просто открытые «Лаборатории» — мягкое peek-состояние
									// (две активные пилюли смотрелись конфликтом, владелец
									// 2026-08-14).
									inGroup ? 'active' : expanded ? 'is-peek' : ''
								}`}
								onClick={() => setOpenedGroup(expanded ? null : { id: item.id, at: normalizedPath })}
								aria-expanded={expanded}
								title={t(item.labelKey)}
							>
								<span className="nav-item-box">
									<span className="nav-icon" style={{ '--icon': `url(${item.icon})` } as React.CSSProperties} />
									<span className="nav-label">{t(item.labelKey)}</span>
	<span className="nav-subtree__chevron" aria-hidden="true" />
								</span>
							</button>
							<div className="nav-subtree__body">
								<div className="nav-subtree__items">
									{children.map((child) => (
										<NavLink
											key={child.path}
											to={child.path}
											// Вкладка носит ДОП-стиль sub-active (тинт + чёрточка); основную
											// пилюлю — родитель-группа (владелец 2026-08-14). Активность — по адресу вкладки
											// («Карточка товара» — на обоих своих маршрутах).
											className={() => `nav-item nav-item--sub ${isPathOf(child) ? 'sub-active' : ''}`}
											onClick={() => {
												if (isMobile) onClose()
											}}
											title={t(child.labelKey)}
										>
											<span className="nav-item-box">
												<span className="nav-icon" style={{ '--icon': `url(${child.icon})` } as React.CSSProperties} />
												<span className="nav-label">{t(child.labelKey)}</span>
											</span>
											{navBadge(navBadges, child.id) && <span className="nav-newtag" aria-label="Новый раздел">new</span>}
										</NavLink>
									))}
								</div>
							</div>
						</div>
					)
				}
				const hasBadge = navBadge(navBadges, item.id)
				return (
				<NavLink
					key={item.path}
					to={item.path}
					className={({ isActive }) => {
						// nav-item--new: у нового раздела своя тёплая подложка в тон стикеру —
						// без неё ярлык висел в пустоте (владелец, 10.09).
						return `nav-item ${isActive ? 'active' : ''} ${hasBadge ? 'nav-item--new' : ''}`
					}}
					onClick={() => {
						if (isMobile) onClose()
					}}
					title={t(item.labelKey)}
				>
					<span className="nav-item-box">
						{item.colored ? (
							<img src={item.icon} alt="" className="nav-icon nav-icon--colored" />
						) : (
							<span className="nav-icon" style={{ '--icon': `url(${item.icon})` } as React.CSSProperties} />
						)}
						<span className="nav-label">{t(item.labelKey)}</span>
						{/* Маркер нового раздела — угловая накладка в пилюле пункта. Слово
						    «new» занимало 40px и обрезало длинные названия («Умные помощники»),
						    а линия-порт у Мастерской читалась как посторонний рисунок; накладка
						    ширины не ест, потому что лежит абсолютом (владелец, 10.09). */}
					</span>
					{/* Ярлык нового раздела — вне .nav-item-box: у того overflow: hidden,
					    и «хвостик» пузыря срезало бы. Абсолют внутри строки: ширины не
					    занимает, поэтому длинные названия («Умные помощники») целы —
					    прежняя плашка в потоке съедала 40px и резала их (владелец, 10.09). */}
					{hasBadge && (
						<span className="nav-newtag" aria-label="Новый раздел">new</span>
					)}
				</NavLink>
				)
			})}
		</>
	)

	return (
		<>
			<div className={`sidebar-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />

			<aside className={`sidebar ${isOpen ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
				<div className="sidebar-header">
					<NavLink to="/" className="logo" onClick={onClose} title="Umnik AI" aria-label="Umnik AI">
						<img src="/icons/logo.svg" alt="" className="logo-icon" />
						<span className="logo-text">Umnik AI</span>
					</NavLink>

					<button
						className="sidebar-toggle"
						onClick={handleToggle}
						title={isMobile ? t('nav.closeMenu') : collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
						type="button"
						aria-label={isMobile ? t('nav.closeMenu') : collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
					>
						<img src={icon('sidebar')} alt="" width="18" height="18" />
					</button>
				</div>

				<nav className={`sidebar-nav ${isCompactNav ? 'sidebar-nav--compact' : ''}`}>
					{isCompactNav ? (
						<>
							<NavGroup
								title={t('nav.groupAccount')}
								iconSrc={icon('user')}
								open={navGroupsOpen.account}
								onToggle={() => toggleNavGroup('account')}
							>
								{accountItems}
							</NavGroup>
							<NavGroup
								title={t('nav.groupTools')}
								iconSrc="/icons/blocks_lk.svg"
								open={navGroupsOpen.tools}
								onToggle={() => toggleNavGroup('tools')}
							>
								{toolsItems}
							</NavGroup>
						</>
					) : (
						<>
							{accountItems}
							<div className="sidebar-nav-border"></div>
							{toolsItems}
							<div className="sidebar-nav-border"></div>
						</>
					)}
				</nav>

				<SidebarChats
					collapsed={collapsed}
					userExists={Boolean(user)}
					sessions={sessions}
					currentSessionId={currentSessionId}
					onOpenSession={handleOpenSession}
					onDeleteSession={deleteOne}
							onPinSession={pinOne}
							currentAgentSlug={currentAgentSlug}
					onRenameSession={renameOne}
					hasMore={hasMore}
					onLoadMore={loadMore}
				/>

				<div className="sidebar-footer">
					{collapsed ? (
						<>
							<NavLink
								to="/tariffs"
								state={{ backgroundLocation: location }}
								className="sidebar-token-btn"
								onClick={onClose}
								title={t('nav.choosePlan')}
							>
								<BalanceCoin size={22} />
							</NavLink>
					</>
				) : (
						<div className="sidebar-footer-row">
							<NavLink
								to="/tariffs"
								state={{ backgroundLocation: location }}
								className="sidebar-tariff-btn"
								onClick={onClose}
							>
								{t('nav.tariffs')}
							</NavLink>
							{!isMobile && (
								<a
									href="https://telegram.me/partnershipsads"
									target="_blank"
									rel="noopener noreferrer"
									className="sidebar-support-btn"
									title={t('nav.support')}
								>
									<img src="/icons/consultant.svg" alt="" width="20" height="20" />
								</a>
							)}
						</div>
					)}

					{isMobile ? (
						<div className="sidebar-quick-mobile">
							<SidebarQuickMenu
								user={user}
								theme={theme}
								onAccountClick={handleAccountClick}
								onToggleNotifications={toggleNotifications}
								notificationsOpen={notificationsOpen}
								hasUnreadNotifications={unreadCount > 0}
								onCloseSidebar={onClose}
								onLogout={handleLogout}
						isSidebarOpen={isOpen}
							/>
							<NotificationPanel open={notificationsOpen} onClose={closeNotifications} />
						</div>
					) : (
						<div className="sidebar-header-actions">
							<HeaderActions user={user} theme={theme} onAccountClick={handleAccountClick} />
						</div>
					)}
				</div>
			</aside>
		</>
	)
}

export default Sidebar
